import { RiInformationLine } from '@react-icons/all-files/ri/RiInformationLine';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Button } from '@/components/atoms/Button/Button';
import { MessageBar } from '@/components/atoms/MessageBar/MessageBar';
import { RadioGroup } from '@/components/atoms/RadioSwitch';
import { RadioInput } from '@/components/atoms/RadioSwitch/RadioInput';
import { Li, P } from '@/components/atoms/Typography/Typography';
import { ModalDrawerDialog } from '@/components/molecules/ModalDrawerDialog/ModalDrawerDialog';
import nameSpacesConfig from '@/config/config.nameSpaces.json';
import {
  formFields,
  formValues,
  FormValues,
} from '@/containers/ConfigurePage/components/FormConfig/formData';
import { getCountryConfigFromLocale } from '@/lib/i18n/getCountryConfigFromLocale';
import { useData } from '@/lib/pageData/pageDataContext';
import { Countries } from '@/types/Countries';
import { SubscriptionType } from '@/utils/fetch/fetchProductSubscriptionByRegion';
import { formatPrice } from '@/utils/formatPrice';
import { useScrollToFormFieldError } from '@/utils/useScrollToFormFieldError';
import { useHeavyUserRate } from '../../hooks/useHeavyUserRate';
import styles from './HeavyUser.module.scss';

interface Props {
  disabledLabel?: React.ReactNode;
  subscriptions: SubscriptionType[];
}

type HeavyUseFeeData = {
  default: number;
} & Partial<Record<Countries, number>>;

export const HEAVY_USE_FEE: HeavyUseFeeData = {
  DK: 3750,
  default: 500,
};

export const HeavyUser = ({ subscriptions, disabledLabel }: Props) => {
  const { locale } = useRouter();
  const { currency } = useData<'default'>();
  const { control, formState } = useFormContext<FormValues>();
  const { t } = useTranslation([
    nameSpacesConfig.pages,
    nameSpacesConfig.common,
  ]);
  const { additional } = useHeavyUserRate(subscriptions);
  const [isHeavyUseTooltipOpen, setIsHeavyUseTooltipOpen] = useState(false);
  const { ref } = useScrollToFormFieldError<HTMLInputElement>(
    formFields.heavyUse
  );

  const { countryCode } = getCountryConfigFromLocale(locale!);

  return (
    <Controller
      control={control}
      name={formFields.heavyUse}
      rules={{ required: true }}
      render={({ field }) => {
        return (
          <>
            <div className={styles.wrapper}>
              <RadioGroup
                data-test-id="heavy-use-radio-group"
                label={t('configure.heavyUser.label')}
                labelChildren={
                  formState.errors.heavyUse ? (
                    <span className={styles.error}>
                      *{t('configure.heavyUser.required')}
                    </span>
                  ) : undefined
                }
                version="level3"
                onChange={field.onChange}
                aria-invalid={!!formState.errors.heavyUse}
                aria-required="true"
                value={field.value}
                name={field.name}
              >
                <p className={styles.subLabel}>
                  {t([
                    `configure.heavyUser.${locale}.question`,
                    `configure.heavyUser.question`,
                  ])}
                  <Button
                    aria-label={t('common.general.moreInfo')}
                    onClick={() => setIsHeavyUseTooltipOpen(true)}
                  >
                    <RiInformationLine size={20} />
                  </Button>
                </p>
                <div className={styles.group}>
                  <RadioInput
                    aria-label={t('configure.heavyUser.button1')}
                    value={formValues.heavyUse.yes}
                    className={styles.radioInput}
                  >
                    <span className={styles.radioText}>
                      <span>{t('configure.heavyUser.button1')}</span>
                      {!disabledLabel && (
                        <span className={styles.price}>
                          +{additional}{' '}
                          {t(`configure.heavyUser.additionalMonthlyCost`)}
                        </span>
                      )}
                    </span>
                  </RadioInput>
                  <RadioInput
                    aria-label={t('configure.heavyUser.button2')}
                    value={formValues.heavyUse.no}
                    className={styles.radioInput}
                    ref={ref}
                  >
                    <span>{t('configure.heavyUser.button2')}</span>
                  </RadioInput>
                </div>
              </RadioGroup>
              {field.value === formValues.heavyUse.no && (
                <p>
                  {t('configure.heavyUser.penalty', {
                    fee: formatPrice(
                      HEAVY_USE_FEE[countryCode] ?? HEAVY_USE_FEE.default,
                      locale!,
                      currency,
                      false
                    ),
                  })}
                </p>
              )}
              {disabledLabel && field.value === formValues.heavyUse.yes && (
                <MessageBar
                  data-test-id="disabled-heavy-use-message-bar"
                  type="warning"
                >
                  {disabledLabel}
                </MessageBar>
              )}
            </div>
            <ModalDrawerDialog
              labels={{
                ariaLabel: t('common:modal.info.label'),
                closeButtonLabel: t('common:drawer.close'),
                submitButtonLabel: t('common:modal.info.submit'),
              }}
              isOpen={isHeavyUseTooltipOpen}
              onDismiss={() => setIsHeavyUseTooltipOpen(false)}
              heading={t('configure.heavyUser.modal.title')}
            >
              <div className={styles.modalContent}>
                <P className={styles.modalContentLabel}>
                  {t('configure.heavyUser.modal.description')}
                </P>
                <P className={styles.modalContentLabel}>
                  {t('configure.heavyUser.modal.list.title')}
                </P>
                <ul>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Li key={i + 1}>
                      {t(`configure.heavyUser.modal.list.items.${i + 1}`)}
                    </Li>
                  ))}
                </ul>
              </div>
            </ModalDrawerDialog>
          </>
        );
      }}
    />
  );
};
